import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api';

interface RateLimitingConfig {
  can_disable: boolean;
  can_change: boolean;
  requests_per_minute: {
    min: number;
    max: number;
    default: number;
  };
  tokens_per_minute: {
    min: number;
    max: number;
    default: number;
  };
}

interface NetworkingStepProps {
  provider: string;
  model: string;
  initialRequestsPerMinute?: number;
  initialTokensPerMinute?: number;
  onChange: (data: { requestsPerMinute: number; tokensPerMinute: number }) => void;
  onNext: () => void;
  onBack: () => void;
  onCancel: () => void;
}

const NetworkingStep: React.FC<NetworkingStepProps> = ({
  provider,
  model,
  initialRequestsPerMinute = 60,
  initialTokensPerMinute = 90000,
  onChange,
  onNext,
  onBack,
  onCancel
}) => {
  const [requestsPerMinute, setRequestsPerMinute] = useState(initialRequestsPerMinute);
  const [tokensPerMinute, setTokensPerMinute] = useState(initialTokensPerMinute);
  const [enableRateLimiting, setEnableRateLimiting] = useState(true);
  const [rateLimitConfig, setRateLimitConfig] = useState<RateLimitingConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRateLimitConfig();
  }, [provider, model]);

  const loadRateLimitConfig = async () => {
    try {
      const response = await apiClient.get('/pipes/config');
      const config = response.data;
      
      // Find the provider and model
      const providerConfig = config.llm?.providers?.find((p: any) => p.enum_value === provider);
      if (providerConfig) {
        const modelConfig = providerConfig.models?.find((m: any) => m.value === model);
        if (modelConfig?.rate_limiting) {
          const rlConfig = modelConfig.rate_limiting;
          setRateLimitConfig(rlConfig);
          
          // Set defaults from config
          setRequestsPerMinute(rlConfig.requests_per_minute.default);
          setTokensPerMinute(rlConfig.tokens_per_minute.default);
          setEnableRateLimiting(!rlConfig.can_disable);
          
          // Notify parent of defaults
          onChange({
            requestsPerMinute: rlConfig.requests_per_minute.default,
            tokensPerMinute: rlConfig.tokens_per_minute.default
          });
        }
      }
    } catch (error) {
      console.error('Failed to load rate limit config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestsChange = (value: string) => {
    const num = parseInt(value, 10) || 0;
    
    // Enforce min/max if config is available
    let finalValue = num;
    if (rateLimitConfig && rateLimitConfig.can_change) {
      const min = rateLimitConfig.requests_per_minute.min;
      const max = rateLimitConfig.requests_per_minute.max;
      finalValue = Math.max(min, Math.min(max, num));
    }
    
    setRequestsPerMinute(finalValue);
    onChange({ requestsPerMinute: finalValue, tokensPerMinute });
  };

  const handleTokensChange = (value: string) => {
    const num = parseInt(value, 10) || 0;
    
    // Enforce min/max if config is available
    let finalValue = num;
    if (rateLimitConfig && rateLimitConfig.can_change) {
      const min = rateLimitConfig.tokens_per_minute.min;
      const max = rateLimitConfig.tokens_per_minute.max;
      finalValue = Math.max(min, Math.min(max, num));
    }
    
    setTokensPerMinute(finalValue);
    onChange({ requestsPerMinute, tokensPerMinute: finalValue });
  };

  const handleToggleRateLimiting = (enabled: boolean) => {
    setEnableRateLimiting(enabled);
    if (!enabled) {
      onChange({ requestsPerMinute: 0, tokensPerMinute: 0 });
    } else {
      onChange({ requestsPerMinute, tokensPerMinute });
    }
  };

  const handleNext = () => {
    if (enableRateLimiting) {
      onChange({ requestsPerMinute, tokensPerMinute });
    } else {
      onChange({ requestsPerMinute: 0, tokensPerMinute: 0 });
    }
    onNext();
  };

  return (
    <div className="card shadow">
      <div className="card-body">
        <h4 className="mb-3">
          <i className="fas fa-network-wired mr-2"></i>
          Networking & Rate Limiting
        </h4>

        <p className="text-muted mb-4">
          Configure rate limiting to control how your LLM pipe handles request throughput.
          This helps prevent exceeding provider quotas and manages costs.
        </p>

        {loading && (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading configuration...</span>
            </div>
          </div>
        )}

        {!loading && (
          <>
            <div className="form-group">
              <div className="custom-control custom-switch">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="enableRateLimiting"
                  checked={enableRateLimiting}
                  onChange={(e) => handleToggleRateLimiting(e.target.checked)}
                  disabled={rateLimitConfig ? !rateLimitConfig.can_disable : false}
                />
                <label className="custom-control-label" htmlFor="enableRateLimiting">
                  Enable Rate Limiting
                  {rateLimitConfig && !rateLimitConfig.can_disable && (
                    <span className="badge badge-warning ml-2">Required</span>
                  )}
                </label>
              </div>
              <small className="form-text text-muted">
                {rateLimitConfig && !rateLimitConfig.can_disable
                  ? 'Rate limiting is required for this model and cannot be disabled.'
                  : 'When enabled, requests will be throttled to prevent exceeding configured limits.'}
              </small>
            </div>

        {enableRateLimiting && (
          <>
            <div className="alert alert-info">
              <i className="fas fa-info-circle mr-2"></i>
              <strong>Rate Limiting Benefits:</strong>
              <ul className="mb-0 mt-2">
                <li>Prevent exceeding provider API quotas</li>
                <li>Control costs by limiting token usage</li>
                <li>Improve system stability with predictable load</li>
                <li>Avoid rate limit errors from LLM providers</li>
              </ul>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label htmlFor="requestsPerMinute">
                    Requests Per Minute
                    <span className="text-danger ml-1">*</span>
                    {rateLimitConfig && !rateLimitConfig.can_change && (
                      <span className="badge badge-secondary ml-2">Fixed</span>
                    )}
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    id="requestsPerMinute"
                    value={requestsPerMinute}
                    onChange={(e) => handleRequestsChange(e.target.value)}
                    min={rateLimitConfig?.requests_per_minute.min || 1}
                    max={rateLimitConfig?.requests_per_minute.max || 10000}
                    disabled={rateLimitConfig ? !rateLimitConfig.can_change : false}
                    required
                  />
                  <small className="form-text text-muted">
                    {rateLimitConfig && rateLimitConfig.can_change
                      ? `Allowed range: ${rateLimitConfig.requests_per_minute.min} - ${rateLimitConfig.requests_per_minute.max} (default: ${rateLimitConfig.requests_per_minute.default}) • ~${(requestsPerMinute / 60).toFixed(1)} requests per second`
                      : rateLimitConfig && !rateLimitConfig.can_change
                      ? `Fixed at ${rateLimitConfig.requests_per_minute.default} requests per minute for this model • ~${(requestsPerMinute / 60).toFixed(1)} requests per second`
                      : `Maximum number of requests allowed per minute (default: 60) • ~${(requestsPerMinute / 60).toFixed(1)} requests per second`}
                  </small>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label htmlFor="tokensPerMinute">
                    Tokens Per Minute
                    <span className="text-danger ml-1">*</span>
                    {rateLimitConfig && !rateLimitConfig.can_change && (
                      <span className="badge badge-secondary ml-2">Fixed</span>
                    )}
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    id="tokensPerMinute"
                    value={tokensPerMinute}
                    onChange={(e) => handleTokensChange(e.target.value)}
                    min={rateLimitConfig?.tokens_per_minute.min || 1000}
                    max={rateLimitConfig?.tokens_per_minute.max || 1000000}
                    step="1000"
                    disabled={rateLimitConfig ? !rateLimitConfig.can_change : false}
                    required
                  />
                  <small className="form-text text-muted">
                    {rateLimitConfig && rateLimitConfig.can_change
                      ? `Allowed range: ${rateLimitConfig.tokens_per_minute.min.toLocaleString()} - ${rateLimitConfig.tokens_per_minute.max.toLocaleString()} (default: ${rateLimitConfig.tokens_per_minute.default.toLocaleString()}) • ~${Math.round(tokensPerMinute / 60).toLocaleString()} tokens per second`
                      : rateLimitConfig && !rateLimitConfig.can_change
                      ? `Fixed at ${rateLimitConfig.tokens_per_minute.default.toLocaleString()} tokens per minute for this model • ~${Math.round(tokensPerMinute / 60).toLocaleString()} tokens per second`
                      : `Maximum tokens processed per minute (default: 90,000) • ~${Math.round(tokensPerMinute / 60).toLocaleString()} tokens per second`}
                  </small>
                </div>
              </div>
            </div>

            <div className="alert alert-warning mt-3">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              <strong>Provider Limits:</strong> Make sure these values are within your LLM provider's tier limits.
              Exceeding provider quotas may result in throttling or additional charges.
            </div>
          </>
        )}

        {!enableRateLimiting && (
          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle mr-2"></i>
            <strong>No Rate Limiting:</strong> Requests will be sent to the LLM provider without throttling.
            This may result in rate limit errors if your usage exceeds provider quotas.
          </div>
        )}
          </>
        )}

        <div className="d-flex justify-content-between mt-4">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onBack}
          >
            <i className="fas fa-arrow-left mr-1"></i>
            Back
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleNext}
          >
            Next
            <i className="fas fa-arrow-right ml-1"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default NetworkingStep;
